import React, { useState, useLayoutEffect, useEffect } from 'react';
import styled from 'styled-components/macro';
import { Button, Switch } from '@cognite/cogs.js';
import { notification } from 'antd';
import GraphiQL from 'graphiql';
import GraphiQLExplorer from 'graphiql-explorer';
import {
  buildClientSchema,
  GraphQLSchema,
  getIntrospectionQuery,
} from 'graphql';
import { useGraphQLFetcher } from 'hooks/useGraphQL';
import { URL_DATA_INGESTION_MANUAL } from 'utils/config';
import { useStoreState, useStoreActions } from 'hooks';

type Props = {
  url: string;
  defaultQuery?: string;
  headers?: { [key in string]: string };
};

export const GraphqlPlayground = ({ defaultQuery, url, headers }: Props) => {
  let graphiql: any = null;
  const [explorerIsOpen, setExplorerIsOpen] = useState(true);
  const [schema, setSchema] = useState<GraphQLSchema>();
  const [explorerQuery, setExplorerQuery] = useState(defaultQuery);
  const apiVersion = useStoreState((state) => state.version.apiVersion);
  const setApiVersion = useStoreActions(
    (actions) => actions.version.setApiVersion
  );

  const handleToggleExplorer = () => setExplorerIsOpen(!explorerIsOpen);

  const handleEditQuery = (query: string | undefined) =>
    setExplorerQuery(query);

  const isQueryDefined =
    explorerQuery !== undefined && explorerQuery.trim().length !== 0;

  const graphqlFetcher = useGraphQLFetcher(url);

  useEffect(() => {
    if (isQueryDefined) {
      graphqlFetcher(
        {
          query: getIntrospectionQuery(),
        },
        { headers, shouldPersistHeaders: true }
      ).then((result) => {
        if (!result.data) {
          notification.error({
            message: 'Something went wrong with your request.',
            description: result.message,
            duration: null,
          });
        }
        if (result.data) {
          setSchema(buildClientSchema(result.data));
        }
      });
    }
  }, [isQueryDefined, graphqlFetcher, url, headers, apiVersion]);

  useLayoutEffect(() => {
    if (isQueryDefined) {
      try {
        graphiql?.handlePrettifyQuery();
      } catch {
        // noop
      }
    }
  }, [isQueryDefined, graphiql]);
  return (
    <QueryExplorerContainer>
      <div
        style={{ height: '100%', display: 'flex' }}
        className="graphiql-container"
      >
        <GraphiQLExplorer
          schema={schema}
          query={explorerQuery}
          title="Query Constructor"
          onEdit={handleEditQuery}
          explorerIsOpen={explorerIsOpen}
          onToggleExplorer={handleToggleExplorer}
        />
        <GraphiQL
          ref={(ref) => {
            graphiql = ref;
          }}
          fetcher={(params, opts) =>
            graphqlFetcher(params, {
              shouldPersistHeaders: true,
              ...opts,
              headers: { ...opts?.headers, ...headers },
            })
          }
          query={explorerQuery}
          headerEditorEnabled
          onEditQuery={handleEditQuery}
        >
          <GraphiQL.Toolbar>
            <GraphiQL.Button
              onClick={() => graphiql?.handlePrettifyQuery()}
              label="Prettify"
              title="Prettify Query (Shift-Ctrl-P)"
            />
            <GraphiQL.Button
              onClick={() => graphiql?.handleToggleHistory()}
              label="History"
              title="Show History"
            />
            <GraphiQL.Button
              onClick={handleToggleExplorer}
              label="Query Constructor"
              title="Toggle Constructor"
            />
            <StyledGraphiQLUtilSection>
              <Button
                size="medium"
                type="ghost"
                icon="ExternalLink"
                onClick={() =>
                  window.open(URL_DATA_INGESTION_MANUAL, '_blank', 'noopener')
                }
              >
                How to ingest data
              </Button>
              <Switch
                onChange={(_e, nextValue) => setApiVersion(nextValue ? 3 : 1)}
                name="v3"
                checked={apiVersion === 3}
              />
            </StyledGraphiQLUtilSection>
          </GraphiQL.Toolbar>
        </GraphiQL>
      </div>
    </QueryExplorerContainer>
  );
};

const StyledGraphiQLUtilSection = styled.div`
  display: flex;
  align-items: center;
`;

const QueryExplorerContainer = styled.div`
  height: 100%;

  .doc-explorer-title-bar,
  .history-title-bar {
    height: auto;
  }

  .graphiql-explorer-actions {
    select {
      margin: 0 10px !important;
    }
  }

  input {
    min-width: 70px !important;
  }

  .toolbar-button {
    background: transparent;
    box-shadow: none;
  }

  .graphiql-explorer-root {
    flex-direction: column-reverse !important;
    > div {
      overflow: auto !important;
    }
  }

  .graphiql-explorer-actions {
    border: none !important;
  }

  .graphiql-operation-title-bar input {
    width: auto !important;
    padding-top: 10px;
  }

  .graphiql-explorer-graphql-arguments > div {
    padding: 3px 0;
  }

  .graphiql-explorer-node {
    padding: 3px 0;
  }

  .graphiql-explorer-root {
    padding: 10px 15px !important;
  }
`;
